/**
 * The composer's copy rows: who else gets the message besides the To line.
 *
 * Two of them. Cc is a copy every recipient can see. Bcc - a blind copy - goes
 * to its addresses without anyone else on the letter learning that it did; the
 * usual case is keeping a colleague or an archive address in the loop on a
 * reply to a client, without showing the client an internal address.
 *
 * An open row is drawn above the subject and the toggle that opens it sits on
 * the subject row, so the state lives in one hook and both halves read it.
 */
import { useCallback, useState } from "react";
import { Button } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { RecipientField, asRecipients, type Recipient } from "./RecipientField";

/** In the order the rows are drawn: the visible copy first, as in any mail client. */
const KINDS = ["cc", "bcc"] as const;
export type CopyKind = (typeof KINDS)[number];

const NONE: Record<CopyKind, Recipient[]> = { cc: [], bcc: [] };
const CLOSED: Record<CopyKind, boolean> = { cc: false, bcc: false };

export interface CopyRecipients {
  list: Record<CopyKind, Recipient[]>;
  open: Record<CopyKind, boolean>;
  setList: (kind: CopyKind, next: Recipient[]) => void;
  setOpen: (kind: CopyKind, open: boolean) => void;
  /** Empty and folded away again: what every new draft starts from. Stable. */
  reset: () => void;
}

export function useCopyRecipients(initial?: { cc: string[]; bcc: string[] }): CopyRecipients {
  // A draft being continued brings its copies back, and a row that has
  // addresses in it opens with them - hiding them would hide who gets the letter.
  const [list, setLists] = useState<Record<CopyKind, Recipient[]>>(() =>
    initial ? { cc: asRecipients(initial.cc), bcc: asRecipients(initial.bcc) } : NONE,
  );
  // Both rows stay folded away until asked for. They are used on a small
  // minority of messages, and a permanently visible empty row is a question
  // every writer has to answer with "no" before getting to the subject.
  const [open, setOpens] = useState<Record<CopyKind, boolean>>(() =>
    initial ? { cc: initial.cc.length > 0, bcc: initial.bcc.length > 0 } : CLOSED,
  );
  const setList = useCallback((kind: CopyKind, next: Recipient[]) => {
    setLists((prev) => ({ ...prev, [kind]: next }));
  }, []);
  const setOpen = useCallback((kind: CopyKind, value: boolean) => {
    setOpens((prev) => ({ ...prev, [kind]: value }));
  }, []);
  const reset = useCallback(() => {
    setLists(NONE);
    setOpens(CLOSED);
  }, []);
  return { list, open, setList, setOpen, reset };
}

/** The open copy rows, drawn between the To row and the subject. */
export function CopyRows({ copies }: { copies: CopyRecipients }) {
  const t = useT();
  return (
    <>
      {KINDS.map((kind) =>
        copies.open[kind] ? (
          <RecipientField
            key={kind}
            label={t(`compose.${kind}`)}
            value={copies.list[kind]}
            onChange={(next) => copies.setList(kind, next)}
            autoFocus
            onEmptyBackspaceExit={() => copies.setOpen(kind, false)}
          />
        ) : null,
      )}
    </>
  );
}

/**
 * The toggles that open a folded copy row, one per row still folded.
 *
 * They sit on the subject row rather than the To row, because a reply has no To
 * row and the controls still have to be there.
 */
export function CopyToggles({ copies }: { copies: CopyRecipients }) {
  const t = useT();
  return (
    <>
      {KINDS.map((kind) =>
        copies.open[kind] ? null : (
          <Button
            key={kind}
            type="button"
            variant="ghost"
            size="sm"
            className="h-auto flex-none rounded-full px-2.5 py-1 text-[11.5px] font-[650] text-muted-foreground hover:bg-[var(--crm-sunk)] hover:text-foreground"
            // "Bcc" is a term many people have never had explained, so the
            // toggle says what it does before anyone has to guess.
            title={kind === "bcc" ? t("compose.bccHint") : undefined}
            onClick={() => copies.setOpen(kind, true)}
          >
            {t(`compose.${kind}`)}
          </Button>
        ),
      )}
    </>
  );
}
